<script setup lang="ts">
import { ApiError } from '@/api/client'
import { acceptInvite } from '@/api/team'
import { useAuthStore } from '@/stores/auth'
import type { Provider, ProviderUser } from '@/types'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

/**
 * Pública: el token de la URL **es** la credencial. No lleva `publicOnly` —
 * alguien con sesión abierta puede estar aceptando una invitación a otra cuenta.
 */
definePage({
  meta: { layout: 'blank', public: true },
})

const route = useRoute('accept-invite-token')
const router = useRouter()
const auth = useAuthStore()

const password = ref('')
const confirmPassword = ref('')
const isPasswordVisible = ref(false)
const loading = ref(false)
const error = ref('')

const token = computed(() => String(route.params.token || ''))

const passwordRulesOk = computed(() => password.value.length >= 6 && /[A-Z]/.test(password.value))
const passwordsMatch = computed(() => !!password.value && password.value === confirmPassword.value)

const submit = async () => {
  error.value = ''
  if (!token.value) {
    error.value = 'Missing invitation token.'

    return
  }
  if (!passwordRulesOk.value) {
    error.value = 'Password must be at least 6 characters and include one uppercase letter.'

    return
  }
  if (!passwordsMatch.value) {
    error.value = 'Passwords do not match.'

    return
  }

  loading.value = true
  try {
    const session = await acceptInvite(token.value, password.value)

    auth.setSession(
      session.provider as unknown as Provider,
      session.providerUser as unknown as ProviderUser,
      session.token.accessToken,
      session.token.refreshToken,
    )
    await router.replace({ name: 'dashboard' })
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : 'Could not accept invitation.'
  }
  finally {
    loading.value = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-md-none d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    class="auth-wrapper"
    no-gutters
  >
    <AuthIllustration />

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            Accept your invitation 🤝
          </h4>
          <p class="mb-0">
            Choose a password to join your team on Blue Trails.
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="submit">
            <VRow>
              <VCol
                v-if="error"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                  density="compact"
                >
                  {{ error }}
                </VAlert>
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="password"
                  autofocus
                  label="New password"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="new-password"
                  hint="6+ characters, with at least one uppercase letter."
                  persistent-hint
                  :disabled="loading"
                  :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />
              </VCol>

              <VCol cols="12">
                <VTextField
                  v-model="confirmPassword"
                  label="Confirm password"
                  placeholder="············"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="new-password"
                  :disabled="loading"
                />
              </VCol>

              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                  :loading="loading"
                >
                  Accept invitation
                </VBtn>
              </VCol>

              <VCol
                cols="12"
                class="text-center text-base"
              >
                <span>Link expired?</span>
                <RouterLink
                  class="text-primary d-inline-block ms-1"
                  :to="{ name: 'login' }"
                >
                  Log in
                </RouterLink>
                <span class="ms-1">and ask your team owner for a new invitation.</span>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth";
</style>
